/**
 * CoWork file source — adapts a device working-file (read over the desktop
 * bridge) to the unified file-preview module's {@link FileSource} contract, so
 * CoWork reuses the same dialog + renderers (pdf, image, code, csv, markdown,
 * html, …) as the rest of the app. Desktop-only.
 *
 * @module components/cowork/cowork-file-source
 */

import { getDesktop } from "@/lib/desktop";
import type { FileSource } from "@/components/file-preview";

const EXT_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  html: "text/html",
  htm: "text/html",
  csv: "text/csv",
  md: "text/markdown",
  txt: "text/plain",
  json: "application/json",
};

function mimeFor(name: string): string | undefined {
  return EXT_MIME[name.toLowerCase().split(".").pop() ?? ""];
}

function b64ToBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8.buffer;
}

/** Build a preview source for a device working file at `relPath`. */
export function coworkFileSource(relPath: string): FileSource {
  const filename = relPath.split(/[\\/]/).filter(Boolean).pop() ?? relPath;
  const contentType = mimeFor(filename);
  let textP: Promise<string> | undefined;
  let bytesP: Promise<ArrayBuffer> | undefined;

  const loadBytes = () =>
    (bytesP ??= (getDesktop()?.cowork.readFileBuffer(relPath) ?? Promise.resolve({})).then((r) =>
      b64ToBuffer((r as { base64?: string }).base64 ?? ""),
    ));

  return {
    id: `cowork:${relPath}`,
    filename,
    contentType,
    capabilities: { canDownload: false, canOpenExternal: false },
    getText: () =>
      (textP ??= (getDesktop()?.cowork.readFile(relPath) ?? Promise.resolve({})).then(
        (r) => (r as { content?: string }).content ?? "",
      )),
    getBytes: loadBytes,
    getBlobUrl: async () => {
      const buf = await loadBytes();
      return URL.createObjectURL(new Blob([buf], contentType ? { type: contentType } : undefined));
    },
  };
}
